import { useTranslations } from "adapters/i18n";
import SwitchCustomizado from "features/shared/components/SwitchCustomizado";
import { TruncatedTextWithTooltip } from "features/shared/components/TruncatedTextWithTooltip";
import React, { useMemo } from "react";
import UserActionsMenu from "../components/userActionsMenu";
import type { User } from "../userList.types";
import { UserListColumns } from "../userList.utils";

type UseUserListColumnsProps = {
  onChangeStatus: (user: User) => void;
  onCopyLink: (user: User) => Promise<void>;
  onEdit: (user: User) => void;
  onSendLink: (user: User) => Promise<void>;
};

export function useUserListColumns({
  onChangeStatus,
  onCopyLink,
  onEdit,
  onSendLink,
}: UseUserListColumnsProps) {
  const { t } = useTranslations();

  const columns = useMemo(
    () => [
      {
        id: UserListColumns.Name,
        titulo: t("user:full-name"),
        ordenavel: false,
        pegaValor: (row: User) => row.name,
      },
      {
        id: UserListColumns.Login,
        titulo: t("user:login"),
        ordenavel: false,
        pegaValor: (row: User) => row.login,
      },
      {
        id: UserListColumns.Email,
        titulo: t("common:fields.email"),
        ordenavel: false,
        pegaValor: (row: User) => row.email,
      },
      {
        id: UserListColumns.Farms,
        titulo: t("locales:farms-with-access"),
        ordenavel: false,
        pegaValor: (row: User) => (
          <TruncatedTextWithTooltip childrenText={row.farms} />
        ),
      },
      {
        id: UserListColumns.Retreats,
        titulo: t("locales:retreats"),
        ordenavel: false,
        pegaValor: (row: User) => (
          <TruncatedTextWithTooltip childrenText={row.retreats} />
        ),
      },
      {
        id: UserListColumns.Permissions,
        titulo: t("user:access-permissions"),
        ordenavel: false,
        pegaValor: (row: User) => (
          <TruncatedTextWithTooltip childrenText={row.permissions} />
        ),
      },
      {
        id: UserListColumns.Status,
        titulo: `${t("common:status.active")}/${t("common:status.inactive")}`,
        ordenavel: false,
        pegaValor: (row: User) => (
          <SwitchCustomizado
            checked={row.isActiveForClient}
            handleClick={() => onChangeStatus(row)}
          />
        ),
      },
      {
        id: UserListColumns.Actions,
        titulo: t("common:action-plural"),
        ordenavel: false,
        pegaValor: (row: User) => (
          <UserActionsMenu
            user={row}
            onEdit={onEdit}
            onSendLink={onSendLink}
            onCopyLink={onCopyLink}
          />
        ),
      },
    ],
    [onChangeStatus, onCopyLink, onEdit, onSendLink, t]
  );

  return { columns };
}
